import { markUpdateReady, watchForSafeMoments } from './updates'
import { whenNetworkQuiet } from './networkPriority'

/**
 * How often a long-open tab asks whether a new build has shipped.
 *
 * ⚠️ IT WAS 30 MINUTES, AND A STAFF CONSOLE LEFT OPEN RAN THE OLD BUILD FOR UP
 * TO HALF AN HOUR AFTER A DEPLOY — a fix that had shipped looked like it had
 * not. The check is one small revalidated `sw.js` request, so it also runs on a
 * route change (at most once a minute); applying the update still waits for a
 * safe moment (`updates.ts`), so a child mid-round is never reloaded.
 */
const UPDATE_CHECK_MS = 5 * 60 * 1000
const ROUTE_CHECK_GAP_MS = 60 * 1000

/** A chunk-load reload counts as "just happened" for this long, so a broken deploy cannot loop. */
const RELOAD_GUARD_MS = 15 * 1000
const RELOAD_KEY = 'kalqy.chunkReloadAt'

/** Must match `public/sw-update-protocol.js`. */
const PROTOCOL_CACHE = 'kalqy-update-protocol-v2'

/**
 * Registers the service worker and keeps the page on the current build.
 *
 * 🔴 A SCHOOL LAPTOP KEEPS ONE TAB OPEN ALL DAY. The injected `registerSW.js`
 * this replaces checked for a new build only when the page loaded, and a hash
 * router never reloads the page — so a classroom could run Monday's build on
 * Friday. Checking every half hour and whenever the tab comes back into view
 * is what lets a deploy actually arrive.
 *
 * ⚠️ OFF IN DEV, where there is no worker (`devOptions.enabled: false`) and
 * Vite's own HMR is the update mechanism.
 */
export function startServiceWorker(): void {
  if (import.meta.env.DEV || typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return

  recoverFromMissingChunks()
  watchForSafeMoments()

  /*
   * 🔴 THIS PAGE SPEAKS THE WAIT-THEN-TAKE-OVER PROTOCOL — say so to the worker.
   * `public/sw-update-protocol.js` lets a new worker wait only when some page
   * has opened this cache; a page from an older build never did, and a worker
   * waiting under it would wait for ever.
   */
  if (typeof caches !== 'undefined') {
    caches.open(PROTOCOL_CACHE).catch(() => {
      /* Storage blocked: the worker then takes over at once, as before. */
    })
  }

  /*
   * 🔴 ONLY A CHANGE OF CONTROLLER IS AN UPDATE. On the very first visit the
   * worker claims a page that had none, which fires the same event — reloading
   * then would cost a first-time visitor a second load for nothing.
   *
   * ⚠️ THAT FIRST CLAIM IS SKIPPED ONCE, NOT FOR THE LIFE OF THE PAGE. Testing
   * caught the version that read "had a controller at startup": a tab opened on
   * a laptop's first-ever visit then ignored every later update for as long as
   * it stayed open — all day, on a school laptop.
   */
  let controlled = navigator.serviceWorker.controller !== null
  let asked = false
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (!controlled) {
      controlled = true
      return
    }
    /* We asked, at a safe moment — go onto the new build now. */
    if (asked) {
      window.location.reload()
      return
    }
    /* Taken over without our asking (another tab, or an older protocol): reload at the next safe moment. */
    markUpdateReady()
  })

  /*
   * 🔴 REGISTERED WHEN THE NETWORK IS QUIET, NOT ON BOOT. On a first visit,
   * registering starts a ~60 MB precache that took the whole classroom link
   * while a teacher's chosen game was trying to download (`networkPriority`).
   * A returning page is already controlled from navigation, so waiting costs it
   * nothing but a later update check.
   */
  whenNetworkQuiet()
    .then(() => navigator.serviceWorker.register('/sw.js', { scope: '/' }))
    .then((registration) => {
      /* A new worker is installed and waiting: take it at the next safe moment. */
      const offer = (worker: ServiceWorker | null): void => {
        if (!worker || navigator.serviceWorker.controller === null) return
        markUpdateReady(() => {
          asked = true
          worker.postMessage({ type: 'SKIP_WAITING' })
        })
      }
      /*
       * ⚠️ A NEW WORKER CAN ALREADY BE PART-WAY IN. Registration waits for a
       * quiet network, and the browser checks for updates on its own meanwhile —
       * so one may be installing, or already waiting, by the time this runs, and
       * its `updatefound` has fired with nobody listening. Watch whichever exists
       * now as well as whatever comes next.
       */
      const watch = (worker: ServiceWorker | null): void => {
        if (!worker) return
        if (worker.state === 'installed') {
          offer(worker)
          return
        }
        worker.addEventListener('statechange', () => {
          if (worker.state === 'installed') offer(worker)
        })
      }
      watch(registration.waiting)
      watch(registration.installing)
      registration.addEventListener('updatefound', () => {
        watch(registration.installing)
      })

      requestPersistentStorage()

      let lastCheck = Date.now()
      const check = (): void => {
        lastCheck = Date.now()
        registration.update().catch(() => {
          /* Offline, or the origin is down — the next check will try again. */
        })
      }
      window.setInterval(check, UPDATE_CHECK_MS)
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') check()
      })
      window.addEventListener('hashchange', () => {
        if (Date.now() - lastCheck >= ROUTE_CHECK_GAP_MS) check()
      })
    })
    .catch(() => {
      /* No worker means no offline cache, not a broken app — the page still runs. */
    })
}

/**
 * 🔴 THE CHUNK THAT NO LONGER EXISTS. Between a deploy and this tab's reload, a
 * lazily-loaded screen can ask for a file from the previous build. Vite reports
 * that as `vite:preloadError`; one reload puts the tab on the build that does
 * have the file. The guard stops a genuinely broken deploy reloading forever.
 */
function recoverFromMissingChunks(): void {
  window.addEventListener('vite:preloadError', (event) => {
    let last = 0
    try {
      last = Number(sessionStorage.getItem(RELOAD_KEY) ?? 0)
    } catch {
      /* Storage blocked — fall through and reload once. */
    }
    if (Date.now() - last < RELOAD_GUARD_MS) return
    event.preventDefault()
    try {
      sessionStorage.setItem(RELOAD_KEY, String(Date.now()))
    } catch {
      /* Without storage the guard is weaker, but one reload is still right. */
    }
    window.location.reload()
  })
}

/**
 * Asks the browser to keep this site's caches when the laptop runs low on space.
 *
 * 🔴 WITHOUT IT A GAME'S FILES ARE "BEST EFFORT". The browser evicts the
 * least-recently-used site first when storage is tight (web.dev, "Storage for the
 * web"), and on a shared school laptop that can be Kalqy — so the next session
 * re-downloads every game on classroom wifi.
 *
 * ⚠️ CHROMIUM ONLY, BECAUSE THERE IT IS SILENT. Chrome, Edge and ChromeOS decide
 * from engagement without asking anyone; Firefox shows a permission prompt, and
 * a pop-up in front of a four-year-old is worse than a re-download. Ephemeral
 * ChromeOS sessions wipe everything at sign-out regardless — that is a fleet
 * setting for the school, covered in the IT guide.
 */
function requestPersistentStorage(): void {
  const nav = navigator as Navigator & { userAgentData?: unknown }
  if (!nav.userAgentData || typeof nav.storage?.persist !== 'function') return
  nav.storage
    .persisted()
    .then((already) => (already ? true : nav.storage.persist()))
    .catch(() => {
      /* Refused or unsupported: the caches stay best-effort, as before. */
    })
}

